import { useEffect, useState } from 'react';
import { CheckCircle2, KeyRound, Plus, RefreshCw, Sparkles, Star, XCircle, Zap } from 'lucide-react';
import { ApiError } from '@/api/client';
import {
  useAiCatalog,
  useAiProviders,
  useAiSettings,
  useDeleteAiProvider,
  useFetchModels,
  useSaveAiProvider,
  useTestAiProvider,
  useUpdateAiSettings,
} from '@/api/ai';
import type { AiProvider, LlmProvider, LlmProviderInfo } from '@/api/types';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  ErrorState,
  Input,
  Modal,
  PageHeader,
  Skeleton,
  Textarea,
  useToast,
} from '@/components/ui';
import { cn } from '@/lib/cn';
import { formatRelative } from '@/lib/format';
import { AiUsageCard } from './AiUsageCard';

const PROVIDER_STYLE: Record<LlmProvider, { mark: string; tint: string }> = {
  anthropic: { mark: 'A', tint: 'from-orange-400 to-amber-600' },
  gemini: { mark: 'G', tint: 'from-sky-400 to-indigo-500' },
  xai: { mark: 'x', tint: 'from-slate-700 to-slate-950' },
  openai: { mark: 'O', tint: 'from-emerald-500 to-teal-700' },
  groq: { mark: 'g', tint: 'from-orange-500 to-red-600' },
  mistral: { mark: 'M', tint: 'from-amber-400 to-orange-600' },
  deepseek: { mark: 'D', tint: 'from-blue-500 to-indigo-700' },
  openrouter: { mark: 'R', tint: 'from-violet-500 to-fuchsia-600' },
  openai_compatible: { mark: '⌘', tint: 'from-slate-400 to-slate-600' },
};

export function ProviderMark({ provider, size = 'md' }: { provider: LlmProvider; size?: 'sm' | 'md' }) {
  const s = PROVIDER_STYLE[provider];
  return (
    <span
      aria-hidden
      className={cn(
        'font-heading inline-flex shrink-0 items-center justify-center rounded-xl bg-gradient-to-br font-bold text-white shadow-sm',
        s.tint,
        size === 'sm' ? 'h-8 w-8 text-sm' : 'h-11 w-11 text-lg',
      )}
    >
      {s.mark}
    </span>
  );
}

export function AiSettingsPage() {
  return (
    <>
      <PageHeader
        title="AI assistant"
        description="Connect the language models your agents use to draft replies and summarise tickets. Keys are encrypted and never shown again."
      />

      <AssistantSettingsCard />

      <AiProvidersSection className="mt-8" />

      <AiUsageCard />
    </>
  );
}

/**
 * The workspace's Claude / Gemini / … connections: add, edit, test, make default, remove.
 * `workspaceId` lets the platform console manage a client's providers without entering it.
 */
export function AiProvidersSection({ workspaceId, className }: { workspaceId?: string; className?: string }) {
  const providers = useAiProviders(workspaceId);
  const catalog = useAiCatalog();
  const [editing, setEditing] = useState<AiProvider | 'new' | null>(null);
  const label = (p: LlmProvider) => catalog.data?.find((c) => c.id === p)?.label ?? p;

  return (
    <section className={className}>
      <div className="mb-3 flex items-end justify-between gap-3">
        <div>
          <h2 className="text-base font-bold">Model providers</h2>
          <p className="text-sm text-slate-500">
            The default provider powers every AI feature. Others are kept ready to switch.
          </p>
        </div>
        <Button onClick={() => setEditing('new')}>
          <Plus className="h-4 w-4" /> Add provider
        </Button>
      </div>

      {providers.isPending ? (
        <div className="grid gap-3 md:grid-cols-2">
          <Skeleton className="h-36 rounded-2xl" />
          <Skeleton className="h-36 rounded-2xl" />
        </div>
      ) : providers.error ? (
        <Card>
          <ErrorState error={providers.error} onRetry={() => void providers.refetch()} />
        </Card>
      ) : providers.data.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Sparkles className="h-6 w-6" />}
            title="No AI provider connected yet"
            description="Add Claude, Gemini, Grok, OpenAI or any OpenAI-compatible endpoint to switch on reply drafts and summaries."
            action={<Button onClick={() => setEditing('new')}>Connect a provider</Button>}
          />
        </Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {providers.data.map((p) => (
            <ProviderCard
              key={p.id}
              provider={p}
              label={label(p.provider)}
              workspaceId={workspaceId}
              onEdit={() => setEditing(p)}
            />
          ))}
        </div>
      )}

      {editing && catalog.data && (
        <ProviderModal
          provider={editing === 'new' ? null : editing}
          catalog={catalog.data}
          workspaceId={workspaceId}
          onClose={() => setEditing(null)}
        />
      )}
    </section>
  );
}

function ProviderCard({
  provider: p,
  label,
  workspaceId,
  onEdit,
}: {
  provider: AiProvider;
  label: string;
  workspaceId?: string;
  onEdit: () => void;
}) {
  const toast = useToast();
  const test = useTestAiProvider(workspaceId);
  const save = useSaveAiProvider(workspaceId);
  return (
    <Card className={cn('flex flex-col p-5', p.isDefault && 'ring-brand-300 ring-2')}>
      <div className="flex items-start gap-3">
        <ProviderMark provider={p.provider} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <p className="truncate font-semibold text-slate-900">{p.name}</p>
            {p.isDefault && (
              <Badge tone="brand">
                <Star className="h-3 w-3 fill-current" /> Default
              </Badge>
            )}
            {!p.isEnabled && <Badge>Disabled</Badge>}
          </div>
          <p className="truncate text-xs text-slate-500">{label}</p>
        </div>
        <Button variant="ghost" size="sm" onClick={onEdit}>
          Edit
        </Button>
      </div>
      <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
        <div className="min-w-0">
          <dt className="text-xs text-slate-500">Model</dt>
          <dd className="truncate font-mono text-[13px] text-slate-800" title={p.model}>
            {p.model}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-slate-500">API key</dt>
          <dd className="flex items-center gap-1 font-mono text-[13px] text-slate-800">
            <KeyRound className="h-3.5 w-3.5 text-slate-400" aria-hidden /> ••••{p.apiKeyLast4}
          </dd>
        </div>
      </dl>
      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
        {p.lastTestOk === true && (
          <span className="flex items-center gap-1 text-xs text-emerald-700">
            <CheckCircle2 className="h-3.5 w-3.5" /> {p.lastTestMessage} · {formatRelative(p.lastTestedAt)}
          </span>
        )}
        {p.lastTestOk === false && (
          <span
            className="flex min-w-0 items-center gap-1 text-xs text-red-700"
            title={p.lastTestMessage ?? ''}
          >
            <XCircle className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{p.lastTestMessage}</span>
          </span>
        )}
        {p.lastTestOk === null && <span className="text-xs text-slate-400">Not tested yet</span>}
        <span className="ml-auto flex gap-2">
          {!p.isDefault && p.isEnabled && (
            <Button
              variant="ghost"
              size="sm"
              loading={save.isPending}
              onClick={() =>
                save.mutate(
                  { id: p.id, input: { isDefault: true } },
                  {
                    onSuccess: () => toast.success(`${p.name} is now the default`),
                    onError: (e) => toast.error(e.message),
                  },
                )
              }
            >
              Make default
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            loading={test.isPending}
            onClick={() =>
              test.mutate(p.id, {
                onSuccess: (r) => (r.ok ? toast.success(`${p.name}: ${r.message}`) : toast.error(r.message)),
                onError: (e) => toast.error(e.message),
              })
            }
          >
            <Zap className="h-3.5 w-3.5" /> Test
          </Button>
        </span>
      </div>
    </Card>
  );
}

function AssistantSettingsCard() {
  const settings = useAiSettings();
  const update = useUpdateAiSettings();
  const toast = useToast();
  const [instructions, setInstructions] = useState('');
  useEffect(() => {
    if (settings.data) setInstructions(settings.data.instructions);
  }, [settings.data]);

  if (settings.isPending) return <Skeleton className="h-64 rounded-2xl" />;
  if (settings.error) {
    return (
      <Card>
        <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />
      </Card>
    );
  }
  const s = settings.data;
  const toggle = (patch: Parameters<typeof update.mutate>[0]) =>
    update.mutate(patch, { onError: (e) => toast.error(e.message) });

  return (
    <Card className="overflow-hidden">
      <div className="from-brand-50/80 to-accent-50/50 flex items-center gap-3 border-b border-slate-100 bg-gradient-to-r px-5 py-4">
        <span className="brand-gradient flex h-10 w-10 items-center justify-center rounded-xl text-white shadow-sm">
          <Sparkles className="h-5 w-5" />
        </span>
        <div className="flex-1">
          <p className="font-semibold text-slate-900">Assistant for agents</p>
          <p className="text-xs text-slate-500">Agents see ✨ Suggest reply and Summarize in every ticket.</p>
        </div>
        <Checkbox
          label="Enabled"
          checked={s.enabled}
          onChange={(e) => toggle({ enabled: e.target.checked })}
        />
      </div>
      <div className={cn('grid gap-5 p-5 lg:grid-cols-[1fr_1.4fr]', !s.enabled && 'opacity-50')}>
        <div className="space-y-3">
          <Checkbox
            label="Reply suggestions"
            description="Drafts the next reply from the conversation. Agents always review before sending."
            checked={s.replySuggestions}
            disabled={!s.enabled}
            onChange={(e) => toggle({ replySuggestions: e.target.checked })}
          />
          <Checkbox
            label="Ticket summaries"
            description="Bullet summary for whoever picks up a long thread."
            checked={s.summaries}
            disabled={!s.enabled}
            onChange={(e) => toggle({ summaries: e.target.checked })}
          />
          <Checkbox
            label="Use the knowledge base"
            description="Grounds drafts in your published help articles."
            checked={s.useKnowledgeBase}
            disabled={!s.enabled}
            onChange={(e) => toggle({ useKnowledgeBase: e.target.checked })}
          />
        </div>
        <div className="space-y-2">
          <Textarea
            label="Brand voice & rules"
            hint="Added to every prompt — tone, languages, things to never promise."
            rows={6}
            disabled={!s.enabled}
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
          />
          <div className="flex justify-end">
            <Button
              size="sm"
              disabled={instructions === s.instructions}
              loading={update.isPending}
              onClick={() =>
                update.mutate(
                  { instructions },
                  {
                    onSuccess: () => toast.success('Instructions saved'),
                    onError: (e) => toast.error(e.message),
                  },
                )
              }
            >
              Save instructions
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}

function ProviderModal({
  provider,
  catalog,
  workspaceId,
  onClose,
}: {
  provider: AiProvider | null;
  catalog: LlmProviderInfo[];
  workspaceId?: string;
  onClose: () => void;
}) {
  const toast = useToast();
  const save = useSaveAiProvider(workspaceId);
  const remove = useDeleteAiProvider(workspaceId);
  const fetchModels = useFetchModels(workspaceId);
  const [kind, setKind] = useState<LlmProvider>(provider?.provider ?? 'anthropic');
  const info = catalog.find((c) => c.id === kind)!;
  const [name, setName] = useState(provider?.name ?? info.label);
  const [apiKey, setApiKey] = useState('');
  const [model, setModel] = useState(provider?.model ?? info.suggestedModel ?? '');
  const [baseUrl, setBaseUrl] = useState(provider?.baseUrl ?? '');
  const [showBaseUrl, setShowBaseUrl] = useState(!!provider?.baseUrl || kind === 'openai_compatible');
  const [isEnabled, setIsEnabled] = useState(provider?.isEnabled ?? true);
  const [isDefault, setIsDefault] = useState(provider?.isDefault ?? false);
  const [models, setModels] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirmDelete, setConfirmDelete] = useState(false);

  const pick = (p: LlmProviderInfo) => {
    setKind(p.id);
    if (!provider) {
      setName(p.label);
      setModel(p.suggestedModel ?? '');
      setModels([]);
    }
    setShowBaseUrl(p.id === 'openai_compatible');
  };

  const loadModels = () => {
    if (!apiKey && !provider) {
      setErrors({ apiKey: 'Paste the API key first' });
      return;
    }
    fetchModels.mutate(
      apiKey
        ? { provider: kind, apiKey, baseUrl: baseUrl || null }
        : { providerId: provider!.id, baseUrl: baseUrl || null },
      {
        onSuccess: (list) => {
          setModels(list);
          toast.success(`${list.length} models available`);
        },
        onError: (e) => toast.error(e.message),
      },
    );
  };

  const submit = () => {
    const next: Record<string, string> = {};
    if (name.trim().length < 2) next.name = 'Give it a name';
    if (!model.trim()) next.model = 'Choose a model';
    if (!provider && apiKey.trim().length < 8) next.apiKey = 'Paste the full API key';
    if (kind === 'openai_compatible' && !baseUrl.trim()) next.baseUrl = 'Required for custom endpoints';
    setErrors(next);
    if (Object.keys(next).length) return;
    const common = {
      name: name.trim(),
      model: model.trim(),
      baseUrl: baseUrl.trim() || null,
      isEnabled,
      isDefault,
    };
    save.mutate(
      provider
        ? { id: provider.id, input: { ...common, ...(apiKey ? { apiKey: apiKey.trim() } : {}) } }
        : { input: { ...common, provider: kind, apiKey: apiKey.trim() } },
      {
        onSuccess: () => {
          toast.success(provider ? 'Provider saved' : `${common.name} connected — run a test to confirm`);
          onClose();
        },
        onError: (e) => {
          if (e instanceof ApiError && Object.keys(e.fieldErrors).length) setErrors(e.fieldErrors);
          else toast.error(e.message);
        },
      },
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={provider ? `Edit ${provider.name}` : 'Connect an AI provider'}
      description="The key is encrypted on the server and only the last 4 characters are ever shown."
      footer={
        <>
          {provider && (
            <Button
              variant={confirmDelete ? 'danger' : 'ghost'}
              className="mr-auto"
              loading={remove.isPending}
              onClick={() =>
                confirmDelete
                  ? remove.mutate(provider.id, {
                      onSuccess: () => (toast.success('Provider removed'), onClose()),
                      onError: (e) => toast.error(e.message),
                    })
                  : setConfirmDelete(true)
              }
            >
              {confirmDelete ? 'Confirm remove' : 'Remove'}
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={save.isPending} onClick={submit}>
            {provider ? 'Save' : 'Connect'}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {!provider && (
          <fieldset>
            <legend className="mb-2 text-sm font-medium text-slate-700">Provider</legend>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {catalog.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  aria-pressed={kind === p.id}
                  onClick={() => pick(p)}
                  className={cn(
                    'flex items-center gap-2.5 rounded-xl border p-2.5 text-left text-sm transition-all',
                    kind === p.id
                      ? 'border-brand-400 bg-brand-50/60 ring-brand-500/15 ring-4'
                      : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50',
                  )}
                >
                  <ProviderMark provider={p.id} size="sm" />
                  <span className="leading-tight font-medium text-slate-800">{p.label}</span>
                </button>
              ))}
            </div>
          </fieldset>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Display name"
            required
            value={name}
            error={errors.name}
            onChange={(e) => setName(e.target.value)}
          />
          <Input
            label="API key"
            type="password"
            autoComplete="off"
            required={!provider}
            placeholder={provider ? `•••• ${provider.apiKeyLast4} — leave blank to keep` : info.keyHint}
            hint={
              info.docsUrl ? (
                <a
                  href={info.docsUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-brand-600 hover:underline"
                >
                  Get a key →
                </a>
              ) : undefined
            }
            error={errors.apiKey}
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
          />
        </div>

        <div>
          <div className="flex items-end gap-2">
            <Input
              label="Model"
              required
              list="ai-models"
              placeholder={info.suggestedModel ?? 'Fetch the list or type a model ID'}
              value={model}
              error={errors.model}
              onChange={(e) => setModel(e.target.value)}
              wrapperClassName="flex-1"
              className="font-mono text-[13px]"
            />
            <Button
              variant="secondary"
              className="mb-px"
              loading={fetchModels.isPending}
              onClick={loadModels}
            >
              <RefreshCw className="h-4 w-4" /> Fetch models
            </Button>
          </div>
          <datalist id="ai-models">
            {models.map((m) => (
              <option key={m} value={m} />
            ))}
          </datalist>
          {models.length > 0 && (
            <div className="mt-2 flex max-h-28 flex-wrap gap-1.5 overflow-y-auto">
              {models.slice(0, 60).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setModel(m)}
                  className={cn(
                    'rounded-lg border px-2 py-1 font-mono text-[11px]',
                    m === model
                      ? 'border-brand-400 bg-brand-50 text-brand-700'
                      : 'border-slate-200 text-slate-600 hover:bg-slate-50',
                  )}
                >
                  {m}
                </button>
              ))}
            </div>
          )}
        </div>

        {showBaseUrl ? (
          <Input
            label="Base URL"
            required={kind === 'openai_compatible'}
            placeholder={info.baseUrl ?? 'http://localhost:11434/v1'}
            hint="OpenAI-compatible endpoint, e.g. Ollama, Together, vLLM, a gateway or proxy."
            error={errors.baseUrl}
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
          />
        ) : (
          <button
            type="button"
            onClick={() => setShowBaseUrl(true)}
            className="text-xs font-medium text-slate-500 hover:text-slate-800"
          >
            + Use a custom endpoint / proxy
          </button>
        )}

        <div className="flex flex-wrap gap-6 border-t border-slate-100 pt-4">
          <Checkbox label="Enabled" checked={isEnabled} onChange={(e) => setIsEnabled(e.target.checked)} />
          <Checkbox
            label="Use as default"
            description="Powers reply drafts and summaries."
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
          />
        </div>
      </div>
    </Modal>
  );
}
